const ACTION_REQUIRED_STATUSES = new Set([
  "QRCODE",
  "DISCONNECTED",
  "TIMEOUT"
]);

const sameId = (left, right) => String(left) === String(right);

export const normalizeWhatsAppStatus = status => {
  const normalized = String(status || "").trim().toUpperCase();
  return normalized === "QRCODE" ? "qrcode" : normalized;
};

export const isWhatsAppInitializingStatus = status => {
  const normalized = normalizeWhatsAppStatus(status);
  return normalized === "OPENING" || normalized === "AUTHENTICATED";
};

const normalizeWhatsApp = whatsApp => {
  if (!whatsApp) return whatsApp;

  return {
    ...whatsApp,
    status: normalizeWhatsAppStatus(whatsApp.status)
  };
};

const updateTime = whatsApp => {
  const value = Date.parse(whatsApp?.updatedAt);
  return Number.isFinite(value) ? value : null;
};

export const shouldApplyWhatsAppUpdate = (current, incoming) => {
  if (!current) return true;

  const currentTime = updateTime(current);
  const incomingTime = updateTime(incoming);

  if (currentTime !== null && incomingTime !== null) {
    return incomingTime >= currentTime;
  }

  return true;
};

const upsertWhatsApp = (state, incoming, partial = false) => {
  if (!incoming?.id) return state;

  const index = state.findIndex(item => sameId(item.id, incoming.id));
  const current = index >= 0 ? state[index] : null;

  if (!shouldApplyWhatsAppUpdate(current, incoming)) {
    return state;
  }

  const next = normalizeWhatsApp(
    partial && current ? { ...current, ...incoming } : incoming
  );

  if (index < 0) return [...state, next];

  return state.map((item, itemIndex) => itemIndex === index ? next : item);
};

export const whatsAppsReducer = (state, action) => {
  if (action.type === "LOAD_WHATSAPPS") {
    return (action.payload || []).reduce(
      (nextState, whatsApp) => upsertWhatsApp(nextState, whatsApp),
      state
    );
  }

  if (action.type === "UPDATE_WHATSAPPS") {
    return upsertWhatsApp(state, action.payload);
  }

  if (action.type === "UPDATE_SESSION") {
    return upsertWhatsApp(state, action.payload, true);
  }

  if (action.type === "DELETE_WHATSAPPS") {
    return state.filter(item => !sameId(item.id, action.payload));
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

export const hasConnectionWarning = whatsApps => {
  const connections = whatsApps || [];

  if (connections.length === 0) return false;

  const defaultConnection = connections.find(whatsApp => whatsApp?.isDefault);

  if (!defaultConnection) return true;

  return ACTION_REQUIRED_STATUSES.has(
    String(normalizeWhatsAppStatus(defaultConnection.status)).toUpperCase()
  );
};
